export type GoogleDirectionResult = google.maps.DirectionsResult;
export type GoogleMap = google.maps.Map;

export enum TravelMode {
  DRIVING = "DRIVING",
  BICYCLING = "BICYCLING",
  DIGITAL = "DIGITAL",
  WALKING = "WALKING",
}

export type MapLocation = {
  formattedAddress: string | undefined;
  latitude: number | null;
  longitude: number | null;
  placeName?: string;
  placeTypes?: string[];
};

export type LatLng = {
  lat: number;
  lng: number;
};

export type TripLegMode =
  | "drive"
  | "fly"
  | "walk"
  | "bike"
  | "train";

export type FlightHop = {
  from: LatLng;
  to: LatLng;
};

export type MapItinerary = {
  locationFrom: MapLocation;
  locationTo: MapLocation;
  destinations?: MapLocation[];
  waypoints?: MapLocation[];
  distanceMeters: number;
  distanceLabel: string;
  durationSeconds: number;
  googleDirection?: GoogleDirectionResult | null;
  googleDirections?: GoogleDirectionResult[];
  flightHops?: FlightHop[];
  flightDistanceMeters?: number;
  flightKgEmission?: number;
  walkDistanceMeters?: number;
  cycleDistanceMeters?: number;
  trainDistanceMeters?: number;
  trainKgEmission?: number;
  /** Negative kg: CO₂ avoided by walk/bike vs driving the same distance. */
  walkBikeAvoidedKgEmission?: number;
  bounds: google.maps.LatLngBounds | null;
};

export type MapCardMarker = {
  experienceId: EXPERIENCE_ID;
  lat: number;
  lng: number;
  circleRadius: number;
  name: string;
  cardId: string;
  embedDirectLink?: string;
};

export type MapIconMarker = {
  experienceId: EXPERIENCE_ID;
  circleRadius: number;
  lat: number;
  lng: number;
  name: string;
};

export type MapExperience = {
  experienceId: EXPERIENCE_ID;
  valueImpact: number;
  defaultZoom?: number;
  cardMarkers: Array<MapCardMarker>;
  iconMarkers: Array<MapIconMarker>;
};

export type MapGeoJSONFeature = {
  type: string;
  geometry: any;
  properties: any;
};

export type MapGeoJSON = {
  type: string;
  fileName: string;
  features: MapGeoJSONFeature[];
};

export type MapRainforestPartner = {
  name: string;
  url?: string;
  logo?: string;
};

export type MapRainforestPhoto = {
  url: string;
  title: string;
  credit?: string;
};

export type MapRainforestVideo = {
  url: string;
  title: string;
  description?: string;
};

export type MapRainforestMarkerDetails = {
  context?: string;
  partners?: string;
  subgrantee?: string;
  forecoContribution?: string;
  highlight?: string;
  localImpact?: string;
  threats?: string;
  support?: string;
  purpose?: string;
  politicalConditions?: string;
  judicialEconomicConditions?: string;
  photos?: MapRainforestPhoto[];
  videos?: MapRainforestVideo[];
};

export type MapRainforestMarker = {
  partners: MapRainforestPartner[];
  lng: number;
  lat: number;
  name: string;
  states?: string;
  picture?: string;
  videoUrl?: string;
  pictureDescription?: string;
  pictureCredit?: string;
  threats?: string[];
  challenges?: string[];
  contribution?: string;
  products?: string[];
  subgrantees?: MapRainforestPartner[];
  details?: MapRainforestMarkerDetails;
};

export type MapRainforestProduct = {
  code: string;
  tags: string[];
  label?: string;
  achievements?: string[];
  description: string;
  videoUrl?: string;
  photos?: MapRainforestPhoto[];
  videos?: MapRainforestVideo[];
};

export type MapProtectedRainforestArea = {
  name: string;
  areaStrokeColor: string;
  areaFillColor: string;
  geoJSON: MapGeoJSON;
  squareFootage?: number;
  picture?: string;
  details?: MapRainforestMarkerDetails;
  defaultHidden?: boolean;
};

export type MapProtectedArea = {
  areaId: string;
  projectId: string;
  defaultZoom: number;
  rainforestRegions: MapProtectedRainforestArea[];
  rainforestCorridors?: MapProtectedRainforestArea[];
  rainforestHumans?: MapProtectedRainforestArea[];
  rainforestMarkers: MapRainforestMarker[];
  rainforestProducts?: MapRainforestProduct[];
};

export type EXPERIENCE_ID =
  | "help-norway-court-house"
  | "shed-norway-partner-bergen"
  | "shed-norway-partner-fortum"
  | "shed-norway-partner-glittertind"
  | "shed-norway-partner-help"
  | "shed-norway-partner-hk"
  | "shed-norway-partner-infinitum"
  | "shed-norway-partner-norgesgruppen"
  | "shed-norway-partner-ob"
  | "shed-norway-partner-rema"
  | "shed-norway-rainforest-foundation"
  | "shed-norway-rainforest-partners"
  | "shed-norway-joachim-partners"
  | "my-little-france"
  | "shed-norway-all-partners"
  | "shed-norway-rainforest-partners-fortum-city"
  | "shed-norway-rainforest-partners-fortum-forest"
  | "shed-norway-rainforest-partners-sats-city"
  | "shed-norway-rainforest-partners-sats-forest"
  | "shed-norway-rainforest-partners-norgesgruppen-city"
  | "shed-norway-rainforest-partners-norgesgruppen-forest"
  | "shed-norway-rainforest-partners-schneider-electronics-city"
  | "shed-norway-rainforest-partners-schneider-electronics-forest"
  | "shed-us-california-visit-jtree"
  | "shed-us-kentucky-bourbon"
  | "shed-us-nevada-visit-vegas"
  | "shed-world-one-common-forest"
  | "shed-norway-rainforest-partners-grieg-foundation-city"
  | "shed-norway-rainforest-partners-grieg-foundation-forest"
  | "shed-norway-rainforest-partners-avarn-security-city"
  | "shed-norway-rainforest-partners-avarn-security-forest"
  | "shed-norway-rainforest-foundastion-olav-thon-gruppen-city"
  | "shed-norway-rainforest-foundastion-olav-thon-gruppen-forest"
  | "shed-norway-rainforest-partners-unil-city"
  | "shed-norway-rainforest-partners-unil-forest"
  | "shed-norway-rainforest-partners-orkla-city"
  | "shed-norway-rainforest-partners-orkla-forest";
